// Panel de control del administrador.
//
// Es la pantalla que se abre en la mañana con el café. Todo el diseño
// responde a esa escena: primero lo que está mal, luego cómo van los
// puntos, y hasta abajo lo que hay que resurtir.
//
// Las alertas van arriba de todo a propósito. Si un día algo huele mal, el
// administrador no debería tener que buscarlo.

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeftRight,
  CalendarClock,
  ClipboardList,
  Gauge,
  Loader2,
  Lock,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';

import { posApi, PosApiError } from './api';
import type { ControlPanel, PanelAlerta, PanelPunto } from './types';

const money = (v: string | null | undefined): string =>
  v == null ? '—' : `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

const hora = (iso: string): string =>
  new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

// ── Alertas ─────────────────────────────────────────────────────

function AlertaCard({ a }: { a: PanelAlerta }) {
  const alta = a.severidad === 'alta';
  return (
    <div
      className={`rounded-xl border p-4 ${
        alta ? 'border-red-500/40 bg-red-500/10' : 'border-amber-500/40 bg-amber-500/10'
      }`}
    >
      <div className="flex items-start gap-3">
        <ShieldAlert
          className={`w-5 h-5 mt-0.5 shrink-0 ${alta ? 'text-red-500' : 'text-amber-500'}`}
        />
        <div className="min-w-0">
          <p className="font-semibold text-foreground">{a.titulo}</p>
          <p className="text-sm text-muted-foreground mt-1">{a.detalle}</p>
          {a.tipo === 'consumo_vs_venta' && (
            <p className="text-xs text-muted-foreground mt-2 tabular-nums">
              Hoy {a.datos.ratio_hoy} · promedio {a.datos.ratio_promedio} · comparado contra{' '}
              {a.datos.dias_comparados} días
            </p>
          )}
          {a.tipo === 'cancelaciones' && (
            <p className="text-xs text-muted-foreground mt-2 tabular-nums">
              {a.datos.canceladas} canceladas y {a.datos.corregidas} corregidas de{' '}
              {a.datos.cobros} cobros
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Punto ───────────────────────────────────────────────────────

function PuntoCard({ p }: { p: PanelPunto }) {
  const dif = p.ultimo_cierre ? Number(p.ultimo_cierre.diferencia) : null;
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <span
          className="inline-block w-3 h-3 rounded-full shrink-0"
          style={{ backgroundColor: p.color || 'currentColor' }}
        />
        <p className="font-semibold text-foreground">{p.name}</p>
        {p.dia_cerrado && (
          <span className="text-xs px-2 py-0.5 rounded-full border border-border text-muted-foreground">
            día cerrado
          </span>
        )}
      </div>

      <p className="text-3xl font-bold text-foreground tabular-nums">{money(p.ventas_hoy)}</p>
      <p className="text-xs text-muted-foreground mt-0.5">
        {p.cobros_hoy} {p.cobros_hoy === 1 ? 'cobro' : 'cobros'}
        {p.canceladas_hoy > 0 && ` · ${p.canceladas_hoy} canceladas`}
      </p>

      {p.ultimo_cierre && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Último corte {p.ultimo_cierre.fecha}
          </p>
          <p
            className={`text-sm font-semibold tabular-nums ${
              dif === 0 ? 'text-foreground' : dif! < 0 ? 'text-red-500' : 'text-amber-500'
            }`}
          >
            {dif === 0
              ? 'cuadró'
              : `${dif! < 0 ? 'faltó' : 'sobró'} ${money(String(Math.abs(dif!)))}`}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Vista ───────────────────────────────────────────────────────

export function ControlPanelView() {
  const [panel, setPanel] = useState<ControlPanel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sinPermiso, setSinPermiso] = useState(false);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    posApi
      .controlPanel(7)
      .then((d) => {
        setPanel(d);
        setSinPermiso(false);
      })
      .catch((e: PosApiError) => {
        // 403 no es un error que reportar como falla: es el candado
        // funcionando. Un cajero no debe ver este panel.
        if (e.status === 403 || e.status === 401) setSinPermiso(true);
        else setError(e.message);
      })
      .finally(() => setCargando(false));
  }, []);

  useEffect(cargar, [cargar]);

  // Se refresca solo: es una pantalla que se deja abierta en el mostrador
  // o en la oficina, y un número viejo ahí engaña.
  useEffect(() => {
    const t = window.setInterval(cargar, 60_000);
    return () => window.clearInterval(t);
  }, [cargar]);

  if (sinPermiso) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <Lock className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium text-foreground">Este panel es del administrador</p>
          <p className="text-sm text-muted-foreground mt-1">
            Muestra el dinero de todos los puntos, así que requiere el permiso{' '}
            <code className="text-xs">pos:totals</code>.
          </p>
        </div>
      </div>
    );
  }

  const inv = panel?.inventario;
  const porCaducar = inv?.lotes_por_caducar || [];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
            <Gauge className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground leading-tight">Panel de control</h1>
            <p className="text-sm text-muted-foreground">
              {panel ? `Al ${hora(panel.generado_en)} · se actualiza solo cada minuto` : 'Cargando…'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={cargar}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${cargando ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-500">
          {error}
        </div>
      )}

      {!panel && cargando && (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {panel && (
        <>
          {/* Lo que está mal va primero. */}
          {panel.alertas.length > 0 && (
            <div className="grid gap-3 mb-6">
              {panel.alertas.map((a, i) => (
                <AlertaCard key={`${a.tipo}-${i}`} a={a} />
              ))}
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-5 mb-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Venta de hoy · todos los puntos
            </p>
            <p className="text-4xl font-bold text-foreground tabular-nums">
              {money(panel.venta_total_hoy)}
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {panel.cobros_hoy} {panel.cobros_hoy === 1 ? 'cobro' : 'cobros'}
              {panel.alertas.length === 0 && ' · sin alertas'}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-6">
            {panel.puntos.map((p) => (
              <PuntoCard key={p.location_id} p={p} />
            ))}
          </div>

          {inv?.disponible && (
            <>
              <div className="grid gap-3 sm:grid-cols-3 mb-4">
                <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
                  <ClipboardList className="w-5 h-5 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-2xl font-bold text-foreground tabular-nums">
                      {inv.solicitudes_abiertas ?? 0}
                    </p>
                    <p className="text-xs text-muted-foreground">por resurtir</p>
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
                  <ArrowLeftRight className="w-5 h-5 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-2xl font-bold text-foreground tabular-nums">
                      {inv.traslados_en_transito ?? 0}
                    </p>
                    <p className="text-xs text-muted-foreground">en tránsito sin confirmar</p>
                  </div>
                </div>
                <div
                  className={`rounded-xl border p-4 flex items-center gap-3 ${
                    (inv.lotes_vencidos ?? 0) > 0
                      ? 'border-red-500/40 bg-red-500/10'
                      : 'border-border bg-card'
                  }`}
                >
                  <AlertTriangle
                    className={`w-5 h-5 shrink-0 ${
                      (inv.lotes_vencidos ?? 0) > 0 ? 'text-red-500' : 'text-muted-foreground'
                    }`}
                  />
                  <div>
                    <p className="text-2xl font-bold text-foreground tabular-nums">
                      {inv.lotes_vencidos ?? 0}
                    </p>
                    <p className="text-xs text-muted-foreground">lotes vencidos</p>
                  </div>
                </div>
              </div>

              {porCaducar.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                    <CalendarClock className="w-4 h-4 text-muted-foreground" />
                    Caduca en los próximos 3 días
                  </p>
                  <div className="grid gap-1.5">
                    {porCaducar.map((l, i) => (
                      <div
                        key={i}
                        className={`flex items-center justify-between text-sm rounded-lg px-3 py-2 ${
                          l.vencido ? 'bg-red-500/10' : 'bg-background'
                        }`}
                      >
                        <span className="text-foreground">
                          {l.supply}{' '}
                          <span className="text-muted-foreground">· {l.location}</span>
                        </span>
                        <span className="text-muted-foreground tabular-nums">
                          {Number(l.quantity)} {l.unit} · {l.vencido ? 'vencido' : l.expires_on}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
